import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import { Save, ArrowLeft, UploadCloud } from "lucide-react";
import api from "../../services/api";
import toast from "react-hot-toast";

export default function AddCandidate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]); // Jobs dropdown ke liye

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    job_id: "",
    status: "Shortlisted",
  });

  // File state alag se handle karni hoti hai
  const [resumeFile, setResumeFile] = useState(null);

  // Active jobs fetch karna
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const response = await api.get("/jobs", {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Sirf active jobs dikhayenge dropdown mein
        setJobs(response.data.data.filter(job => job.status === 'active'));
      } catch (error) {
        console.error("Failed to fetch jobs");
      }
    };
    fetchJobs();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 5MB Size Check Frontend par bhi
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size should be less than 5MB");
        e.target.value = null; // Clear input
        return;
      }
      setResumeFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem("admin_token");

      // 🚨 FIle upload ke liye FormData use karna zaroori hai 🚨
      const submitData = new FormData();
      submitData.append("name", formData.name);
      submitData.append("email", formData.email);
      submitData.append("phone", formData.phone);
      submitData.append("job_id", formData.job_id);
      submitData.append("status", formData.status);
      
      if (resumeFile) {
        submitData.append("resume_file", resumeFile);
      }

      await api.post("/candidates", submitData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data" // 👈 Ye header bohot zaroori hai file bhejne ke liye
        },
      });

      toast.success("Candidate added successfully!");
      navigate("/candidates"); // Wapas list par bhej do
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to add candidate.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex items-center justify-between">
        <div>
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-black mb-2 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Candidates
          </button>
          <PageTitle title="Add New Candidate" subtitle="Manually add a candidate and assign them to a job." />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow border border-gray-200 p-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <input type="text" name="name" required value={formData.name} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
              <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
              <input type="text" name="phone" required value={formData.phone} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500" />
            </div>

            {/* Job Selection Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Applying For (Job) *</label>
              <select name="job_id" required value={formData.job_id} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500 bg-white">
                <option value="" disabled>Select a Job</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>

            {/* 👇 SMART FILE UPLOAD INPUT 👇 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resume Upload (Optional)</label>
              <div className="relative">
                <input 
                  type="file" 
                  name="resume_file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" // 👈 Formats restrict kar diye
                  onChange={handleFileChange} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 outline-none file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100 transition-all cursor-pointer text-gray-500" 
                />
              </div>
              <p className="text-xs text-gray-400 mt-1">Supported formats: PDF, DOC, PNG, JPG (Max 5MB)</p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Initial Status</label>
              <select name="status" value={formData.status} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 outline-none focus:ring-2 focus:ring-yellow-500 bg-white">
                <option value="Applied">Applied</option>
                <option value="Shortlisted">Shortlisted</option>
                <option value="Interview Scheduled">Interview Scheduled</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

          </div>

          <div className="pt-6 flex justify-end border-t border-gray-100">
            <button disabled={loading} type="submit" className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2.5 px-8 rounded-lg transition-colors">
              <Save size={18} /> {loading ? "Saving..." : "Save Candidate"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}